import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Pin, ExternalLink, VolumeX, Volume2, FileText, Clock, Eye, Info } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button, buttonVariants } from '@/client/components/ui/button';
import { Badge } from '@/client/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/client/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/client/components/ui/tooltip';
import { cn } from '@/client/lib/utils';
import { useEncodersStore } from '@/client/store/encodersStore';
import type { EncoderAlertPayload } from '@shared/types';

const ALERT_TYPE_LABELS: Record<string, string> = {
  offline: 'Offline',
  zero_bitrate: 'Zero Bitrate',
  bitrate_too_high: 'Bitrate Too High',
  bitrate_drop: 'Bitrate Drop',
  bitrate_unstable: 'Unstable Bitrate',
  frame_rate_drop: 'Frame Rate Drop',
  resolution_change: 'Resolution Changed',
};

/** Resolution criteria for each alert type (must match ANOMALY_CONFIG in triage.ts) */
const RESOLUTION_INFO: Record<string, { consecutive: number; description: string }> = {
  offline: { consecutive: 1, description: 'Resolves immediately when encoder comes back online' },
  zero_bitrate: { consecutive: 1, description: 'Resolves immediately when bitrate > 0' },
  bitrate_too_high: { consecutive: 1, description: 'Resolves immediately when bitrate drops below 5 MB/s' },
  bitrate_drop: { consecutive: 4, description: 'Resolves after 4 consecutive normal readings (~4 min)' },
  bitrate_unstable: { consecutive: 4, description: 'Resolves after 4 consecutive stable readings (~4 min)' },
  frame_rate_drop: { consecutive: 4, description: 'Resolves after 4 consecutive normal readings (~4 min)' },
  resolution_change: { consecutive: 1, description: 'Auto-resolves after 5 consecutive stable readings (~5 min)' },
};

function formatBitrate(bps?: number): string {
  if (!bps && bps !== 0) return '-';
  if (bps === 0) return '0 Mbps';
  const mbps = bps / 1_000_000;
  return `${mbps.toFixed(2)} Mbps`;
}

function formatValue(value?: number, alertType?: string): string {
  if (value === undefined || value === null) return '-';
  if (alertType?.includes('bitrate')) {
    return formatBitrate(value);
  }
  if (alertType === 'frame_rate_drop') {
    return `${value.toFixed(2)} fps`;
  }
  return value.toLocaleString();
}

interface AlertCardProps {
  alert: EncoderAlertPayload;
  isInTriage: boolean;
  onPinToTriage: (encoderId: string) => void;
  isPinning?: boolean;
  isMuted?: boolean;
  onMute?: (encoderId: string, alertType: string) => void;
  onUnmute?: (encoderId: string, alertType: string) => void;
  onExport?: (encoderId: string) => void;
}

export function AlertCard({ alert, isInTriage, onPinToTriage, isPinning, isMuted, onMute, onUnmute, onExport }: AlertCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmingPin, setConfirmingPin] = useState(false);

  // Look up client name from encoders store
  const encoder = useEncodersStore((s) => s.getEncoder(alert.encoderId));
  const clientName = encoder?.clientName;

  const severityStyles = {
    critical: 'border-l-red-500 bg-red-50/10',
    warning: 'border-l-amber-500 bg-amber-50/10',
    info: 'border-l-blue-500 bg-blue-50/10',
  };

  const severityDotStyles = {
    critical: 'bg-red-500',
    warning: 'bg-amber-500',
    info: 'bg-blue-500',
  };

  const severityBadgeStyles = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    info: 'bg-blue-100 text-blue-700 border-blue-200',
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        className={cn(
          'rounded-lg border border-l-4 transition-colors',
          severityStyles[alert.severity]
        )}
      >
        {/* Collapsed Header */}
        <div className="flex items-center gap-3 p-3">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              {isOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>

          {/* Severity dot */}
          <span
            className={cn(
              'h-2.5 w-2.5 rounded-full flex-shrink-0',
              severityDotStyles[alert.severity]
            )}
          />

          {/* Client name + Stream name / Encoder ID */}
          <span className="font-medium text-sm truncate flex-1">
            {clientName && <span className="text-muted-foreground">{clientName} / </span>}
            {alert.streamName || alert.encoderId}
          </span>

          {/* Alert type badge */}
          <Badge variant="outline" className="text-xs">
            {ALERT_TYPE_LABELS[alert.alertType] || alert.alertType}
          </Badge>

          {/* Severity badge */}
          <Badge
            variant="outline"
            className={cn('text-xs capitalize', severityBadgeStyles[alert.severity])}
          >
            {alert.severity}
          </Badge>

          {/* Alert timestamps - show when fired and last seen */}
          {alert.firedAt && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-muted-foreground flex items-center gap-1 cursor-help">
                  <Clock className="h-3 w-3" />
                  <span>Fired {formatDistanceToNow(alert.firedAt, { addSuffix: true })}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                <p>Alert first triggered: {new Date(alert.firedAt).toLocaleString()}</p>
                {RESOLUTION_INFO[alert.alertType] && (
                  <p className="mt-1 text-muted-foreground">{RESOLUTION_INFO[alert.alertType].description}</p>
                )}
              </TooltipContent>
            </Tooltip>
          )}
          {/* Last detected - only show if different from firedAt (condition is ongoing) */}
          {alert.lastAnomalyAt && alert.firedAt && alert.lastAnomalyAt !== alert.firedAt && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-muted-foreground flex items-center gap-1 cursor-help">
                  <Eye className="h-3 w-3" />
                  <span>Last detected {formatDistanceToNow(alert.lastAnomalyAt, { addSuffix: true })}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                <p>Condition last detected: {new Date(alert.lastAnomalyAt).toLocaleString()}</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Mute/Unmute button */}
          {onMute && onUnmute && (
            isMuted ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 text-xs text-muted-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onUnmute(alert.encoderId, alert.alertType);
                }}
                title="Unmute this alert"
              >
                <Volume2 className="h-3 w-3" />
                Unmute
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 text-xs text-muted-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onMute(alert.encoderId, alert.alertType);
                }}
                title="Mute this alert (hide from counts)"
              >
                <VolumeX className="h-3 w-3" />
                Mute
              </Button>
            )
          )}

          {/* Pin to Triage button - two-step confirm */}
          {!isInTriage && (
            confirmingPin ? (
              <Button
                variant="default"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onPinToTriage(alert.encoderId);
                  setConfirmingPin(false);
                }}
                onBlur={() => setConfirmingPin(false)}
                disabled={isPinning}
                autoFocus
              >
                <Pin className="h-3 w-3" />
                {isPinning ? 'Pinning...' : 'Confirm Pin'}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmingPin(true);
                }}
              >
                <Pin className="h-3 w-3" />
                Pin to Triage
              </Button>
            )
          )}
          {isInTriage && (
            <Badge variant="secondary" className="text-xs gap-1">
              <Pin className="h-3 w-3" />
              In Triage
            </Badge>
          )}
        </div>

        {/* Expanded Content */}
        <CollapsibleContent>
          <div className="px-3 pb-3 pt-0 border-t border-dashed ml-9">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-3 text-sm">
              {/* Current Value - hide for bitrate_unstable since it's CV (shown below) */}
              {alert.alertType !== 'bitrate_unstable' && (
                <div>
                  <div className="text-muted-foreground text-xs mb-1">Current</div>
                  <div className="font-mono font-medium">
                    {formatValue(alert.currentValue, alert.alertType)}
                  </div>
                </div>
              )}

              {/* Expected Value - hide for bitrate_unstable since it's CV threshold */}
              {alert.alertType !== 'bitrate_unstable' && (
                <div>
                  <div className="text-muted-foreground text-xs mb-1">Expected</div>
                  <div className="font-mono">
                    {formatValue(alert.expectedValue, alert.alertType)}
                  </div>
                </div>
              )}

              {/* Stats - Mean (always a bitrate value) */}
              {alert.stats?.mean !== undefined && (
                <div>
                  <div className="text-muted-foreground text-xs mb-1">Mean</div>
                  <div className="font-mono">
                    {formatBitrate(alert.stats.mean)}
                  </div>
                </div>
              )}

              {/* Stats - CV */}
              {alert.stats?.cv !== undefined && (
                <div>
                  <div className="text-muted-foreground text-xs mb-1">CV</div>
                  <div className="font-mono">{(alert.stats.cv * 100).toFixed(1)}%</div>
                </div>
              )}
            </div>

            {/* Message */}
            {alert.message && (
              <div className="text-sm text-muted-foreground mb-3">{alert.message}</div>
            )}

            {/* Stats detail row */}
            {alert.stats && (
              <div className="text-xs text-muted-foreground mb-3">
                {alert.stats.sampleCount && (
                  <span>Samples: {alert.stats.sampleCount}</span>
                )}
                {alert.stats.stddev !== undefined && (
                  <span className="ml-4">
                    Stddev: {formatBitrate(alert.stats.stddev)}
                  </span>
                )}
              </div>
            )}

            {/* Resolution criteria */}
            {RESOLUTION_INFO[alert.alertType] && (
              <div className="flex items-start gap-2 text-xs text-muted-foreground mb-3 p-2 bg-muted/30 rounded">
                <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Resolution: </span>
                  {RESOLUTION_INFO[alert.alertType].description}
                  {RESOLUTION_INFO[alert.alertType].consecutive > 1 && (
                    <span className="ml-1 text-muted-foreground/70">
                      (requires {RESOLUTION_INFO[alert.alertType].consecutive} consecutive normal samples)
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              <Link
                to="/encoders/$encoderID"
                params={{ encoderID: alert.encoderId }}
                className={buttonVariants({ variant: 'outline', size: 'sm', className: 'gap-1 text-xs' })}
              >
                <ExternalLink className="h-3 w-3" />
                View Encoder
              </Link>
              {onExport && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 text-xs"
                  onClick={() => onExport(alert.encoderId)}
                >
                  <FileText className="h-3 w-3" />
                  Export Logs
                </Button>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
